import React, { useState } from 'react';
import {
  UserDeleteOutlined,
  MinusCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useUpdatePatientDischargeStatus } from 'infrastructure/hooks/control-panel/use-update-discharge-status';
import {
  useUpdatePatientCcmStatus,
  useAppointmentRequest,
} from 'infrastructure/hooks/control-panel';
import DischargePatientModal from 'components/organisms/controlPanel/dischargePatientModal';
import DischargePatientModalMobile from 'components/organisms/controlPanel/dischargePatientModalMobile';
import useUser from 'utils/useUser';
import forms from 'constants/forms';
import { ModuleType } from 'infrastructure/enums';

import s from './styles.module.scss';

import type { FC } from 'react';

interface RequestButtonsProps {
  patientGuid: string;
  module: ModuleType;
}

export const RequestButtons: FC<RequestButtonsProps> = ({
  patientGuid,
  module,
}) => {
  const { t } = useTranslation();
  const { isMobileDevice } = useUser();

  const [dischargePopupOpen, setDischargePopupOpen] = useState(false);
  const [ccmDischargePopupOpen, setCcmDischargePopupOpen] = useState(false);

  const {
    patientInfo,
    isCombinedModule,
    isRpmModule,
    isCcmModule,
    isBlockCcmModule,
    isReadOnlyRole,
    isCcmActionAllowed,
    isCcmActionDisabled,
    isActiveRpmStatus,
    isEnrolledCcmStatus,
    isBlockRpmModule,
  } = useAccess({
    module,
    patientGuid,
  });

  const { updateDischargeStatus, updateDischargeStatusLoading } =
    useUpdatePatientDischargeStatus({
      onSuccess() {
        setDischargePopupOpen(false);
      },
    });

  const { updateCcmStatus, ccmStatusLoading } = useUpdatePatientCcmStatus({
    onSuccess() {
      setCcmDischargePopupOpen(false);
    },
  });

  const { sendAppointmentRequest, sendAppointmentRequestLoading } =
    useAppointmentRequest();

  const rpmLabel = isActiveRpmStatus
    ? `${t('labels.requestDischarge')}`
    : `${t('labels.dischargeRequested')} ${t('labels.rpm')}`;

  const ccmLabel = isEnrolledCcmStatus
    ? `${t('labels.unenrollPatient')}`
    : `${t('labels.patientUnenrolled')} ${t('labels.ccm')}`;

  const rpmDisable = !isActiveRpmStatus;

  const ccmDisable =
    (isCombinedModule &&
      (isBlockCcmModule || isReadOnlyRole || !isCcmActionAllowed)) ||
    isCcmActionDisabled;

  const rpmRender = isRpmModule || isCombinedModule;
  const ccmRender = isCcmModule || isCombinedModule;

  const appointmentDisabled = isCombinedModule
    ? isReadOnlyRole || isBlockRpmModule || !patientInfo?.appointmentsFlagActive
    : isCcmActionDisabled || !patientInfo?.appointmentsFlagActive;

  return (
    <>
      {rpmRender && !isMobileDevice && (
        <button
          className={s['doc-button']}
          onClick={() => setDischargePopupOpen(true)}
          disabled={rpmDisable || updateDischargeStatusLoading}
          data-cy="control-panel-button-request-discharge-rpm"
        >
          <UserDeleteOutlined className={s['doc-icon']} />
          <span>{rpmLabel}</span>
        </button>
      )}

      {ccmRender && !isMobileDevice && (
        <button
          className={s['doc-button']}
          onClick={() => setCcmDischargePopupOpen(true)}
          disabled={ccmDisable || ccmStatusLoading}
          data-cy="control-panel-button-unenroll-patient-ccm"
        >
          <MinusCircleOutlined className={s['doc-icon']} />
          <span>{ccmLabel}</span>
        </button>
      )}

      <button
        className={s['doc-button']}
        onClick={() =>
          sendAppointmentRequest({
            patientGuid,
            rogerModule: isCcmModule ? ModuleType.CCM : undefined,
          })
        }
        disabled={appointmentDisabled || sendAppointmentRequestLoading}
        data-cy="control-panel-button-next-appointment"
      >
        <CalendarOutlined className={s['doc-icon']} />
        <span>
          {patientInfo?.appointmentsFlagActive
            ? t('labels.requestNextAppointment')
            : t('labels.nextAppointmentRequested')}
        </span>
      </button>

      {dischargePopupOpen &&
        (isMobileDevice ? (
          <DischargePatientModalMobile
            isOpen={dischargePopupOpen}
            toggle={setDischargePopupOpen}
            patientGuid={patientGuid}
            options={forms.patientDischargeStatuses}
            loading={updateDischargeStatusLoading}
            submit={updateDischargeStatus}
            modalType={ModuleType.RPM}
          />
        ) : (
          <DischargePatientModal
            isOpen={dischargePopupOpen}
            toggle={setDischargePopupOpen}
            patientGuid={patientGuid}
            options={forms.patientDischargeStatuses}
            loading={updateDischargeStatusLoading}
            submit={updateDischargeStatus}
            modalType={ModuleType.RPM}
          />
        ))}

      {ccmDischargePopupOpen &&
        (isMobileDevice ? (
          <DischargePatientModalMobile
            isOpen={ccmDischargePopupOpen}
            toggle={setCcmDischargePopupOpen}
            patientGuid={patientGuid}
            options={forms.patientCcmDischargeStatuses}
            loading={ccmStatusLoading}
            submit={updateCcmStatus}
            modalType={ModuleType.CCM}
          />
        ) : (
          <DischargePatientModal
            isOpen={ccmDischargePopupOpen}
            toggle={setCcmDischargePopupOpen}
            patientGuid={patientGuid}
            options={forms.patientCcmDischargeStatuses}
            loading={ccmStatusLoading}
            submit={updateCcmStatus}
            modalType={ModuleType.CCM}
          />
        ))}
    </>
  );
};
